<script setup lang="ts">
import {
  ComponentInternalInstance,
  computed,
  getCurrentInstance,
  inject,
  nextTick,
  onMounted,
  PropType,
  provide,
  ref,
  SetupContext,
  toRaw,
  useAttrs,
  useSlots,
  watch,
} from "vue";
import { ComponentPosition } from "@/models/types";
import { MenuEntry } from "@/models/menu-entry";
import { BcContextMenu, BcButton, BcCollapseTransition } from "..";

defineOptions({ name: "BcMenuItem", inheritAttrs: false });

const attrs: SetupContext["attrs"] = useAttrs();
const slots: SetupContext["slots"] = useSlots();
const currentInstance: ComponentInternalInstance | null = getCurrentInstance();

const modelValue = defineModel<boolean | undefined>({ default: undefined });

const props = defineProps({
  active: { type: Boolean, default: false },
  header: { type: Boolean, default: false },
  componentIndent: { type: Boolean, default: true },
  componentBackground: { type: Boolean, default: true }, // set to false, when component has more child components, TODO: automatic detection
  indent: { type: Boolean, default: false },
  selected: { type: Boolean, default: false },
  beginGroup: { type: Boolean, default: false },
  borderAtTop: { type: Boolean, default: false },
  borderAtBottom: { type: Boolean, default: false },
  componentMaxHeight: { type: String },
  componentPosition: {
    type: String as PropType<ComponentPosition>,
    default: "bottom",
    validator: (value: any) => ComponentPosition.isComponentPosition(value),
  },
  contextMenuActions: {
    type: Array<MenuEntry>,
    default: () => new Array<MenuEntry>(),
  },
  groupId: { type: Number },
  hideOnClose: { type: Boolean, default: true },
  iconSize: {
    type: String,
    default: "1.9311em",
  },
  itemHeight: {
    type: String,
    default: "50px",
  },
  last: { type: Boolean, default: false },
  numberOfVisibleItems: { type: Number },
  readonly: { type: Boolean, default: false },
  transition: { type: Boolean, default: true },
  toggle: { type: Boolean, default: true },
  transitionDuration: { type: Number, default: 500 },
});

const emit = defineEmits<{
  (e: "click", payload: MouseEvent): void;
  (e: "toggle", payload: boolean): void;
  (e: "update:model-value", payload: boolean | undefined): void;
}>();

const rootRef = ref<HTMLDivElement | null>(null);
const componentRef = ref<HTMLDivElement | null>(null);
const contextMenuRef = ref<InstanceType<typeof BcContextMenu> | null>(null);

const isOpen = ref(false);

const showMenu = ref(false);
const menuX = ref(0);
const menuY = ref(0);

const children: ComponentInternalInstance[] = [];
provide("children", children);
const injectedChildren = inject<ComponentInternalInstance[] | null>("children", null);
const injectedBurgerMenuItems = inject<ComponentInternalInstance[] | null>(
  "burgerMenuItems",
  null
);

const active = computed({
  get: () => modelValue.value,
  set: (value) => emit("update:model-value", value),
});
const computedMaxHeight = computed(() => {
  return props.componentMaxHeight
    ? `calc(${props.componentMaxHeight})`
    : props.numberOfVisibleItems
    ? `calc(calc(${props.itemHeight}) * ${props.numberOfVisibleItems})`
    : undefined;
});
const rootClass = computed(() => (attrs.class ? (attrs.class as string).split(" ") : []));
const rootStyle = computed(() => {
  let style = (attrs.style ?? {}) as any;
  style["--icon-size"] = props.iconSize;
  style["--item-level"] = itemLevel.value;
  return style;
});
const inheritedAttrs = computed(() => {
  const { class: string, style: any, ...otherProps } = attrs;
  const inheritedProps = { ...otherProps };
  return inheritedProps;
});
const itemLevel = computed(() => {
  let level = 0;
  if (rootRef.value) {
    let element = rootRef.value.parentElement;
    while (element !== null) {
      if (
        element.classList.contains("bissantzControls") &&
        element.classList.contains("burgerMenuControl")
      ) {
        break;
      }
      if (
        element.classList.contains("bissantzControls") &&
        element.classList.contains("menuItemControl")
      ) {
        level++;
      }
      element = element.parentElement;
    }
  }
  return level;
});

onMounted(() => {
  if (injectedChildren && currentInstance) {
    injectedChildren.push(currentInstance);
  }
  if (injectedBurgerMenuItems && currentInstance) {
    injectedBurgerMenuItems.push(currentInstance);
  }
  if (props.active) {
    isOpen.value = props.active;
    active.value = props.active;
  }
});

function closeAllContextMenu() {
  if (injectedBurgerMenuItems) {
    injectedBurgerMenuItems.forEach((child) => {
      const menuItem = child.proxy as any;
      if (
        menuItem &&
        menuItem.$.exposed &&
        toRaw(child) !== toRaw(currentInstance) &&
        menuItem.$.exposed.showMenu.value
      ) {
        menuItem.$.exposed.showMenu.value = false;
      }
    });
  }
}

function closeAllChildItems(): void {
  // Close all child items
  if (children) {
    children.forEach((child) => {
      const menuItem = child.proxy as any;
      if (
        menuItem &&
        menuItem.$.exposed &&
        menuItem.$slots.component &&
        menuItem.hideOnClose
      ) {
        menuItem.$.exposed.isOpen.value = false;
      }
    });
  }
}

function closeOtherItems(): void {
  if (injectedBurgerMenuItems) {
    injectedBurgerMenuItems.forEach((child) => {
      const menuItem = child.proxy as any;
      if (
        menuItem &&
        menuItem.$.exposed &&
        toRaw(child) !== toRaw(currentInstance) &&
        (typeof props.groupId !== "undefined"
          ? props.groupId !== menuItem.$.exposed.groupId
          : true) &&
        !getChildren(child).find((c) => toRaw(c) === toRaw(currentInstance)) &&
        menuItem.$slots.component
      ) {
        menuItem.$.exposed.isOpen.value = false;
      }
    });
  }
}

function getChildren(componentInstance: ComponentInternalInstance | null) {
  const componentChildren: ComponentInternalInstance[] = [];
  const proxy = componentInstance?.proxy;
  if (proxy && proxy.$.exposed && proxy.$.exposed.children) {
    (proxy.$.exposed.children as ComponentInternalInstance[]).forEach((child) => {
      collectChildren(child, componentChildren);
    });
  }
  return componentChildren;
}

function collectChildren(
  componentInstance: ComponentInternalInstance,
  componentChildren: ComponentInternalInstance[]
) {
  const proxy = componentInstance.proxy;
  componentChildren.push(componentInstance);
  if (proxy && proxy.$.exposed && proxy.$.exposed.children) {
    (proxy.$.exposed.children as ComponentInternalInstance[]).forEach((child) => {
      collectChildren(child, componentChildren);
    });
  }
}

function onContextMenu(event: MouseEvent) {
  event.preventDefault();
  event.stopPropagation();
  closeAllContextMenu();
  if (
    props.contextMenuActions.length &&
    (contextMenuRef.value
      ? !contextMenuRef.value.$el.contains(event.target as Node)
      : true)
  ) {
    showMenu.value = true;
    menuX.value = event.clientX;
    menuY.value = event.clientY;
  }
}

function onClick(payload: MouseEvent): void {
  if (props.toggle && !props.readonly && slots.component) {
    isOpen.value = !isOpen.value;
  }
  emit("click", payload);
}

watch(
  modelValue,
  () => {
    if (modelValue.value !== undefined && modelValue.value !== isOpen.value) {
      isOpen.value = modelValue.value;
    }
  },
  { immediate: true }
);

watch(
  isOpen,
  () => {
    if (modelValue.value !== undefined && modelValue.value !== isOpen.value) {
      modelValue.value = isOpen.value;
    }
    emit("toggle", isOpen.value);
    nextTick(() => {
      if (isOpen.value) {
        closeOtherItems();
      } else {
        closeAllChildItems();
      }
    });
  },
  { immediate: true }
);

defineExpose({
  modelValue,
  active,
  children,
  isOpen,
  showMenu,
});
</script>

<template>
  <div
    @contextmenu.native.prevent="onContextMenu($event)"
    class="bissantzControls menuItemControl"
    :class="[
      {
        beginGroup: beginGroup,
        borderAtTop: borderAtTop,
        borderAtBottom: borderAtBottom,
        last: last,
        opened: $slots.component && isOpen,
        readonly: readonly,
      },
      ...rootClass,
    ]"
    :style="rootStyle"
    ref="rootRef"
  >
    <template v-if="componentPosition === 'top'">
      <template v-if="!toggle">
        <div
          v-if="$slots.component"
          class="component"
          :class="{
            componentIndent: componentIndent,
            componentBackground: componentBackground,
          }"
          :style="{ maxHeight: computedMaxHeight }"
          ref="componentRef"
        >
          <slot name="component"></slot>
        </div>
      </template>
      <template v-else-if="transition">
        <bc-collapse-transition :duration="transitionDuration" v-bind="inheritedAttrs">
          <div
            v-if="$slots.component"
            v-show="isOpen"
            class="component"
            :class="{
              componentIndent: componentIndent,
              componentBackground: componentBackground,
            }"
            :style="{ maxHeight: computedMaxHeight }"
            ref="componentRef"
          >
            <slot name="component"></slot>
          </div>
        </bc-collapse-transition>
      </template>
      <template v-else>
        <div
          v-if="$slots.component && isOpen"
          class="component"
          :class="{
            componentIndent: componentIndent,
            componentBackground: componentBackground,
          }"
          :style="{ maxHeight: computedMaxHeight }"
          ref="componentRef"
        >
          <slot name="component"></slot>
        </div>
      </template>
    </template>
    <bc-button
      class="menuTitle"
      plain
      fullWidth
      v-bind="inheritedAttrs"
      :design="undefined"
      @click="onClick($event)"
    >
      <slot v-if="$slots['title-icon']" name="title-icon"> </slot>
      <div
        class="titleText"
        :class="{
          header: header,
          indent: indent,
          selected: selected,
          noIcon: !$slots['title-icon'],
        }"
      >
        <slot name="title"></slot>
      </div>
    </bc-button>
    <template v-if="componentPosition === 'bottom'">
      <template v-if="!toggle">
        <div
          v-if="$slots.component"
          class="component"
          :class="{
            componentIndent: componentIndent,
            componentBackground: componentBackground,
          }"
          :style="{ maxHeight: computedMaxHeight }"
          ref="componentRef"
        >
          <slot name="component"></slot>
        </div>
      </template>
      <template v-else-if="transition">
        <bc-collapse-transition :duration="transitionDuration" v-bind="inheritedAttrs">
          <div
            v-if="$slots.component"
            v-show="isOpen"
            class="component"
            :class="{
              componentIndent: componentIndent,
              componentBackground: componentBackground,
            }"
            :style="{ maxHeight: computedMaxHeight }"
            ref="componentRef"
          >
            <slot name="component"></slot>
          </div>
        </bc-collapse-transition>
      </template>
      <template v-else>
        <div
          v-if="$slots.component && isOpen"
          class="component"
          :class="{
            componentIndent: componentIndent,
            componentBackground: componentBackground,
          }"
          :style="{ maxHeight: computedMaxHeight }"
          ref="componentRef"
        >
          <slot name="component"></slot>
        </div>
      </template>
    </template>
    <bc-context-menu
      v-if="showMenu"
      :actions="contextMenuActions"
      :x="menuX"
      :y="menuY"
      :iconSize="iconSize"
      position="fixed"
      ref="contextMenuRef"
    />
  </div>
</template>

<style lang="less">
@import "@/styles/components/bc-menu-item.less";
</style>
